/**
 * RFICommentsSection Component
 * Comments thread for RFI discussions
 * BuilderBel Construction Project Management System
 */

import { MessageSquare, Send, Trash2, Edit2, X, Check } from 'lucide-react';
import React, { useState } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { useAuth } from '../../hooks/useAuth';
import {
  useAddRFICommentMutation,
  useUpdateRFICommentMutation,
  useDeleteRFICommentMutation,
} from '../../services/rfiApi';
import type { RFI, RFIComment } from '../../types/rfi';

interface RFICommentsSectionProps {
  projectId: string;
  rfi: RFI;
  onSuccess?: () => void;
}

export const RFICommentsSection: React.FC<RFICommentsSectionProps> = ({
  projectId,
  rfi,
  onSuccess,
}) => {
  const { success, error: showError } = useToast();
  const { user } = useAuth();

  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState('');

  const [addComment, { isLoading: isAdding }] = useAddRFICommentMutation();
  const [updateComment, { isLoading: isUpdating }] = useUpdateRFICommentMutation();
  const [deleteComment, { isLoading: isDeleting }] = useDeleteRFICommentMutation();

  const isLoading = isAdding || isUpdating || isDeleting;

  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
    });
  };

  // Handle add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) return;

    try {
      await addComment({
        projectId,
        rfiId: rfi._id,
        data: {
          content: newComment.trim(),
          isInternal,
        },
      }).unwrap();

      success('Comment added');
      setNewComment('');
      setIsInternal(false);
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to add comment');
    }
  };

  // Handle edit comment
  const handleStartEdit = (comment: RFIComment) => {
    setEditingCommentId(comment._id || '');
    setEditContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditContent('');
  };

  const handleSaveEdit = async () => {
    if (!editingCommentId || !editContent.trim()) return;

    try {
      await updateComment({
        projectId,
        rfiId: rfi._id,
        commentId: editingCommentId,
        data: { content: editContent.trim() },
      }).unwrap();

      success('Comment updated');
      setEditingCommentId(null);
      setEditContent('');
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to update comment');
    }
  };

  // Handle delete comment
  const handleDeleteComment = async (commentId: string) => {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;

    try {
      await deleteComment({
        projectId,
        rfiId: rfi._id,
        commentId,
      }).unwrap();

      success('Comment deleted');
      onSuccess?.();
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } };
      showError(error.data?.message || 'Failed to delete comment');
    }
  };

  // Get initials
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const comments = rfi.comments || [];

  return (
    <div className="space-y-4">
      {/* Comments List */}
      {comments.length > 0 ? (
        <div className="space-y-3 max-h-[400px] overflow-y-auto">
          {comments.map(comment => (
            <div
              key={comment._id}
              className={`p-3 rounded-lg ${
                comment.isInternal ? 'bg-yellow-50 border border-yellow-200' : 'bg-gray-50'
              }`}
            >
              <div className="flex items-start gap-3">
                {/* Avatar */}
                <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-xs font-medium text-brand-700">
                    {getInitials(comment.authorName)}
                  </span>
                </div>

                <div className="flex-1 min-w-0">
                  {/* Header */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {comment.authorName}
                      </span>
                      {comment.isInternal && (
                        <span className="px-1.5 py-0.5 text-[10px] bg-yellow-200 text-yellow-800 rounded">
                          Internal
                        </span>
                      )}
                      <span className="text-xs text-gray-400">{formatDate(comment.createdAt)}</span>
                    </div>

                    {/* Actions */}
                    {comment.authorId === user?.id && !editingCommentId && (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => handleStartEdit(comment)}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Edit"
                        >
                          <Edit2 className="h-3 w-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteComment(comment._id || '')}
                          className="p-1 text-gray-400 hover:text-red-500 rounded"
                          title="Delete"
                        >
                          <Trash2 className="h-3 w-3" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Content */}
                  {editingCommentId === comment._id ? (
                    <div className="mt-2 space-y-2">
                      <textarea
                        value={editContent}
                        onChange={e => setEditContent(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={handleCancelEdit}
                          disabled={isUpdating}
                          className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
                        >
                          <X className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={handleSaveEdit}
                          disabled={!editContent.trim() || isUpdating}
                          className="p-1.5 text-brand-600 hover:text-brand-700 disabled:opacity-50 rounded"
                        >
                          <Check className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <p className="mt-1 text-sm text-gray-600 whitespace-pre-wrap">
                      {comment.content}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="py-6 text-center">
          <MessageSquare className="h-8 w-8 text-gray-300 mx-auto mb-2" />
          <p className="text-sm text-gray-500">No comments yet</p>
        </div>
      )}

      {/* Add Comment */}
      <div className="pt-3 border-t border-gray-200">
        <div className="space-y-2">
          <textarea
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            rows={3}
            placeholder="Add a comment..."
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            disabled={isLoading}
          />

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isInternal}
                onChange={e => setIsInternal(e.target.checked)}
                disabled={isLoading}
                className="rounded border-gray-300 text-brand-600 focus:ring-brand-500"
              />
              <span className="text-xs text-gray-500">
                Internal comment (not visible to external parties)
              </span>
            </label>

            <button
              type="button"
              onClick={handleAddComment}
              disabled={!newComment.trim() || isLoading}
              className="px-3 py-1.5 text-sm bg-brand-600 text-white rounded-lg hover:bg-brand-700 disabled:opacity-50 transition-colors flex items-center gap-1.5"
            >
              {isAdding ? (
                <>
                  <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full" />
                  <span>Adding...</span>
                </>
              ) : (
                <>
                  <Send className="h-3.5 w-3.5" />
                  <span>Add Comment</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RFICommentsSection;
